import type { TabItem } from '../components/Tab/Tab.vue'
import { once } from 'lodash-es'
import { joinURL } from 'ufo'

interface UseLayoutSettings {
  siteName: string
  articlePageUrl: string
  homePageUrl: string
  tabCategories: TabItem[]
  videoPageUrl: string
  siteDomain?: string
  headerLogo?: string
  headerLogoDarkMode?: string
  headerSize?: { width: number; height: number }
  tagline?: string
  footerBrandName?: string
  /** Set when a tenant's legal pages are hosted off-site rather than in-app. */
  privacyPolicyUrl?: string
  termsUrl?: string
  /**
   * Set by tenants that only ever exist as a browser new tab page. Those
   * surfaces title the tab "New Tab" even on the open web, where there are
   * no host browser APIs to detect.
   */
  isNewTabPage?: boolean
}

export const useGlobalLayoutSettings = defineStore('app:layoutSettings', () => {
  const layoutSettings = ref<UseLayoutSettings>({
    siteName: '',
    articlePageUrl: '',
    homePageUrl: '',
    tabCategories: [],
    videoPageUrl: '',
    headerLogo: '',
    headerLogoDarkMode: '',
    tagline: '',
    footerBrandName: '',
  })

  const setup = once((settings: UseLayoutSettings) => {
    layoutSettings.value = {
      ...layoutSettings.value,
      ...settings,
    }
  })

  const settings = computed(() => {
    return layoutSettings.value
  })

  const siteSettingsStore = useSiteSettingsStore()
  const { data: siteSettings } = storeToRefs(siteSettingsStore)

  const filteredTabCategories = computed(() =>
    settings.value.tabCategories.filter(
      (tab) => tab.id !== 'Games' || siteSettings.value.gamesEnabled
    )
  )

  // Read here rather than in the getter below: that getter re-evaluates lazily,
  // and useRuntimeConfig() throws when it runs outside a Nuxt app context.
  const baseURL = useRuntimeConfig().app?.baseURL ?? '/'

  // Kept here so the sidebar and every footer cite the same documents. Tenants
  // that host them off-site set the URLs; the rest fall back to the in-app
  // pages under the base path, so they do not escape it.
  const legalLinks = computed(() => ({
    privacyPolicyUrl:
      settings.value.privacyPolicyUrl ?? joinURL(baseURL, '/privacy-policy'),
    termsUrl: settings.value.termsUrl ?? joinURL(baseURL, '/terms'),
  }))

  return {
    setup,
    settings,
    filteredTabCategories,
    legalLinks,
  }
})
